import React from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  House,
  FolderClosed,
  FileText,
  MessagesSquare,
  Settings,
  X,
} from "lucide-react";
import { useLenderNavigationGuard } from "./LenderNavigationGuardContext";
import CommunicationUnreadBadge from "../../common/CommunicationUnreadBadge";
import useCommunicationUnreadCount from "../../common/useCommunicationUnreadCount";
import "./LenderPlatformSidebar.css";

export default function LenderPlatformSidebar({
  title,
  mobileOpen = false,
  onClose,
}) {
  const { t } = useTranslation("lender-sidebar");
  const location = useLocation();
  const { requestNavigation } = useLenderNavigationGuard();
  const unreadMessagesCount = useCommunicationUnreadCount();

  const handleNavigate = React.useCallback(
    (path) => {
      onClose?.();
      requestNavigation(path);
    },
    [onClose, requestNavigation],
  );

  const items = React.useMemo(
    () => [
      {
        key: "dashboard",
        label: t("items.dashboard", "Dashboard"),
        icon: House,
        path: "/lender/dashboard",
        isActive:
          location.pathname === "/lender" ||
          location.pathname === "/lender/dashboard",
      },
      {
        key: "loan-cases",
        label: t("items.loan-cases", "Loan Cases"),
        icon: FolderClosed,
        path: "/lender/loan-cases",
        isActive: location.pathname.startsWith("/lender/loan-cases"),
      },
      {
        key: "documents",
        label: t("items.documents", "Invoices"),
        icon: FileText,
        path: "/lender/invoices",
        isActive: location.pathname.startsWith("/lender/invoices"),
      },
      {
        key: "communication",
        label: t("items.communication", "Communication"),
        icon: MessagesSquare,
        path: "/lender/communication",
        isActive: location.pathname.startsWith("/lender/communication"),
      },
      {
        key: "account",
        label: t("items.account", "Account"),
        icon: Settings,
        path: "/lender/account",
        isActive: location.pathname.startsWith("/lender/account"),
      },
    ],
    [location.pathname, t],
  );

  const resolvedTitle = title || t("title", "Menu");

  return (
    <>
      <aside className="lender-sidebar">
        <div className="lender-sidebar__panel">
          <div className="lender-sidebar__title">{resolvedTitle}</div>
          <nav
            className="lender-sidebar__nav"
            aria-label={t("aria.desktopNav", "Desktop navigation")}
          >
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className={`lender-sidebar__item ${item.isActive ? "is-active" : ""}`}
                  aria-current={item.isActive ? "page" : undefined}
                >
                  <span className="lender-sidebar__icon-wrap">
                    <Icon size={26} strokeWidth={2.1} />
                    {item.key === "communication" && (
                      <span className="legup-communication-unread-icon-overlay">
                        <CommunicationUnreadBadge count={unreadMessagesCount} />
                      </span>
                    )}
                  </span>
                  <span className="lender-sidebar__label">{item.label}</span>
                  {item.key === "communication" && (
                    <span className="legup-communication-unread-inline">
                      <CommunicationUnreadBadge count={unreadMessagesCount} />
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      <div
        className={`lender-sidebar__mobile-overlay ${mobileOpen ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`lender-sidebar__mobile ${mobileOpen ? "is-open" : ""}`}
        aria-hidden={!mobileOpen}
      >
        <div className="lender-sidebar__mobile-header">
          <span className="lender-sidebar__mobile-title">{resolvedTitle}</span>
          <button
            type="button"
            className="lender-sidebar__mobile-close"
            onClick={onClose}
            aria-label={t("aria.closeMenu", "Close menu")}
          >
            <X size={22} />
          </button>
        </div>
        <nav
          className="lender-sidebar__mobile-nav"
          aria-label={t("aria.mobileNav", "Mobile navigation")}
        >
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleNavigate(item.path)}
                className={`lender-sidebar__mobile-item ${item.isActive ? "is-active" : ""}`}
                aria-current={item.isActive ? "page" : undefined}
              >
                <span className="lender-sidebar__icon-wrap">
                  <Icon size={24} strokeWidth={2.1} />
                </span>
                <span className="lender-sidebar__mobile-label">{item.label}</span>
                {item.key === "communication" && (
                  <CommunicationUnreadBadge count={unreadMessagesCount} />
                )}
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
